import { PersonTypeEnum } from '@zaoblako/shared/modules/client/enums/person.type.enum';
import { PersonTypeEnumI18n } from '@zaoblako/shared/modules/client/i18n/person.type.enum.i18n';
import { PersonPassportDataDto, type PersonPassportDataInterface } from '@zaoblako/shared/modules/client/dtos/person/person-passport-data.dto';
import { TaskContragentBankDetailsFormDto, type TaskContragentBankDetailsFormInterface } from '@zaoblako/shared/modules/task/dtos/contragent/task-contragent-bank-details-form.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { TaskDtoKeysEnum } from '@zaoblako/shared/modules/task/enum/task-dto-keys.enum';
import { idLikeSchema } from '@zaoblako/shared/modules/serde/schemas/id-like.schema';
import { ClientCheckerKeysEnum } from '@zaoblako/shared/modules/client/enums/client-checker-keys.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { russianTinSchema } from '@zaoblako/shared/modules/serde/schemas/russian-tin.schema';
import { russianTrrcSchema } from '@zaoblako/shared/modules/serde/schemas/russian-trrc.schema';
import type { SerdeContextDataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context-data.interface';
import type { SerdeContextInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context.interface';

import * as yup from 'yup';

/** BRS task counterparty (recipient of payment) form - DTO signature */
export interface TaskContragentFormInterface {
  /** Counterparty unique ID (for exists counterparty). If defined then other fields will be ignored. */
  id?: number;
  /** New counterparty name */
  name?: string;
  /** New counterparty person type */
  personType?: PersonTypeEnum;
  /** New counterparty TIN */
  inn?: string;
  /** New counterparty TRRC */
  kpp?: string;
  /**
   * Counterparty passport data (will saved only when {@link TaskContragentFormInterface.personType}
   * equals {@link PersonTypeEnum.INDIVIDUAL_ENTREPRENEUR} or {@link PersonTypeEnum.PRIVATE_CUSTOMER})
   */
  passport?: PersonPassportDataInterface;
  /** Bank details to select and/or save */
  bankDetails?: TaskContragentBankDetailsFormInterface[];
}

const optionalIfIdDefined = (context: SerdeContextInterface<SerdeContextDataInterface, TaskContragentFormInterface>): boolean => Boolean(context?.value?.id);

const REQUIRED_ONLY_ON_NEW_HINT = 'Требуется только для нового контрагента: когда не заполнено поле `id`';

const USING_ONLY_ON_NEW_HINT = 'Использутеся только для нового контрагента: когда не заполнено поле `id`';

const PASSPORT_HINT = `Используется только:
 - Для нового контрагента (когда не заполнено поле \`id\`)
 - Для ИП или физического лица (когда поле \`personType\` равно "${PersonTypeEnum.INDIVIDUAL_ENTREPRENEUR}" или "${PersonTypeEnum.PRIVATE_CUSTOMER}")`;

/** BRS task counterparty (recipient of payment) form - DTO schema */
export const TaskContragentFormDto = SerdeDtoClass.create<TaskContragentFormInterface>(TaskDtoKeysEnum.TASK_CONTRAGENT_FORM_DTO, {
  id: {
    optional: true,
    label: 'Номер контрагента',
    hint: 'Если задан, то будет подставлен существующий контрагент с этим номером, а остальные поля будут проигнорированы',
    serializer: idLikeSchema,
    checkers: [ClientCheckerKeysEnum.CONTRAGENT_ASSERT_EXISTS_BY_ID],
  },
  name: {
    optional: optionalIfIdDefined,
    label: 'Название нового контрагента',
    hint: '',
    serializer: yup
      .string()
      .min(1, DtoMessageEnum.EMPTY_STRING),
  },
  personType: {
    optional: optionalIfIdDefined,
    label: 'Тип нового контрагента',
    hint: REQUIRED_ONLY_ON_NEW_HINT,
    serializer: commonEnumValidator(PersonTypeEnum, PersonTypeEnumI18n)
  },
  inn: {
    optional: optionalIfIdDefined,
    label: 'ИНН нового контрагента',
    hint: REQUIRED_ONLY_ON_NEW_HINT,
    serializer: russianTinSchema,
  },
  kpp: {
    optional: true,
    label: 'ИНН нового контрагента',
    hint: USING_ONLY_ON_NEW_HINT,
    serializer: russianTrrcSchema,
  },
  passport: {
    optional: true,
    label: 'Паспортные данные нового контрагента',
    hint: PASSPORT_HINT,
    nested: PersonPassportDataDto,
    serializer: (_, nested) => nested,
  },
  bankDetails: {
    optional: true,
    nested: TaskContragentBankDetailsFormDto,
    label: 'Рассчетные счета',
    serializer: (_, nested) => yup.array(nested),
  },
});
